/**
 * WorkflowStepFields (Step 3)
 *
 * Required application fields with auto-fill and confidence badges.
 * Substeps: applicant, propertyOwner, projectDescription, tradeSpecific, unknownHandling
 *
 * Part of Phase 8: Job Workflow Wizard
 */

import { useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ConfidenceBadge } from '@/components/ui/confidence-badge';
import { Checkbox } from '@/components/ui/checkbox';
import { User, Home, FileText, Settings, Check, AlertTriangle, HelpCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { UseJobWorkflowReturn } from '@/hooks/useJobWorkflow';
import type { JobData } from '../WorkflowContainer';

interface WorkflowStepFieldsProps {
  jobData: JobData;
  workflow: UseJobWorkflowReturn;
  onUpdate?: (updates: Partial<JobData>) => void;
}

export function WorkflowStepFields({ jobData, workflow, onUpdate }: WorkflowStepFieldsProps) {
  const currentSubstep = workflow.currentSubstepId;

  // Ref to prevent re-render cascades (workflow object is new on every render)
  const lastSubstepsRef = useRef<string>('');

  // Check if trade-specific fields are complete
  const isTradeSpecificComplete = () => {
    const data = jobData as Record<string, unknown>;
    const trade = jobData.trade || jobData.permitType;

    if (trade === 'electrical') {
      return !!(data.load_calculation && data.panel_size);
    } else if (trade === 'mechanical') {
      return !!(data.equipment_model && data.seer_rating && data.tonnage);
    }
    // No trade-specific fields required for other trades
    return true;
  };

  // Mark substeps as complete based on data
  useEffect(() => {
    const tradeSpecificDone = isTradeSpecificComplete();
    const substepKey = [
      (jobData as Record<string, unknown>).applicant_name ? 'applicant' : '',
      jobData.client_name ? 'propertyOwner' : '',
      jobData.description ? 'projectDescription' : '',
      tradeSpecificDone ? 'tradeSpecific' : '',
    ].join(',');

    if (lastSubstepsRef.current !== substepKey) {
      lastSubstepsRef.current = substepKey;
      if ((jobData as Record<string, unknown>).applicant_name) {
        workflow.markSubstepComplete('fields', 'applicant');
      }
      if (jobData.client_name) {
        workflow.markSubstepComplete('fields', 'propertyOwner');
      }
      if (jobData.description) {
        workflow.markSubstepComplete('fields', 'projectDescription');
      }
      if (tradeSpecificDone) {
        workflow.markSubstepComplete('fields', 'tradeSpecific');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobData]);

  const handleUpdate = (field: string, value: unknown) => {
    if (onUpdate) {
      onUpdate({ [field]: value });
    }
  };

  return (
    <div className="space-y-6">
      {/* Applicant Information - This is the CONTRACTOR pulling the permit, NOT the client */}
      <Card className={cn(currentSubstep === 'applicant' && 'ring-2 ring-primary')}>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <User className="h-5 w-5" />
            Applicant Information
            {(jobData as Record<string, unknown>).applicant_name && (
              <Badge variant="outline" className="ml-auto text-green-600 border-green-300 dark:text-green-300 dark:border-green-700">
                <Check className="h-3 w-3 mr-1" />
                Complete
              </Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              The licensed contractor or owner-builder pulling the permit. This is your information, not the client's.
            </p>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="applicantName">Applicant Name <span className="text-destructive">*</span></Label>
                <Input
                  id="applicantName"
                  placeholder="Your full legal name or company name"
                  value={((jobData as Record<string, unknown>).applicant_name as string) || ''}
                  onChange={(e) => handleUpdate('applicant_name', e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="applicantEmail">Email <span className="text-destructive">*</span></Label>
                <Input
                  id="applicantEmail"
                  type="email"
                  placeholder="your@company.com"
                  value={((jobData as Record<string, unknown>).applicant_email as string) || ''}
                  onChange={(e) => handleUpdate('applicant_email', e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="applicantPhone">Phone <span className="text-destructive">*</span></Label>
                <Input
                  id="applicantPhone"
                  type="tel"
                  placeholder="(555) 123-4567"
                  value={((jobData as Record<string, unknown>).applicant_phone as string) || ''}
                  onChange={(e) => handleUpdate('applicant_phone', e.target.value)}
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Client / Property Owner */}
      <Card className={cn(currentSubstep === 'propertyOwner' && 'ring-2 ring-primary')}>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Home className="h-5 w-5" />
            Client / Property Owner
            {jobData.client_name && (
              <Badge variant="outline" className="ml-auto text-green-600 border-green-300 dark:text-green-300 dark:border-green-700">
                <Check className="h-3 w-3 mr-1" />
                From job creation
              </Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              The property owner or client who hired you. Required for Notice of Commencement and owner authorization forms.
            </p>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="ownerName">Client / Owner Name <span className="text-destructive">*</span></Label>
                <Input
                  id="ownerName"
                  placeholder="Property owner's name"
                  value={jobData.client_name || ''}
                  onChange={(e) => handleUpdate('client_name', e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="ownerEmail">Email</Label>
                <Input
                  id="ownerEmail"
                  type="email"
                  placeholder="client@example.com"
                  value={jobData.client_email || ''}
                  onChange={(e) => handleUpdate('client_email', e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="ownerPhone">Phone <span className="text-destructive">*</span></Label>
                <Input
                  id="ownerPhone"
                  type="tel"
                  placeholder="(555) 123-4567"
                  value={jobData.client_phone || ''}
                  onChange={(e) => handleUpdate('client_phone', e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="ownerAddress">Mailing Address</Label>
                <Input
                  id="ownerAddress"
                  placeholder="If different from property address"
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Project Description */}
      <Card className={cn(currentSubstep === 'projectDescription' && 'ring-2 ring-primary')}>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Project Description <span className="text-destructive">*</span>
            {jobData.description && (
              <Badge variant="outline" className="ml-auto text-green-600 border-green-300 dark:text-green-300 dark:border-green-700">
                <Check className="h-3 w-3 mr-1" />
                Complete
              </Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              A clear description of the work to be performed. This appears on permit applications.
            </p>

            <Textarea
              placeholder="Describe the scope of work in detail..."
              value={jobData.description || ''}
              onChange={(e) => handleUpdate('description', e.target.value)}
              rows={4}
            />

            {jobData.jobScope && !jobData.description && (
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleUpdate('description', jobData.jobScope)}
                >
                  Use job scope as description
                </Button>
                <span className="text-sm text-muted-foreground">
                  "{jobData.jobScope}"
                </span>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Trade-Specific Fields */}
      <Card className={cn(currentSubstep === 'tradeSpecific' && 'ring-2 ring-primary')}>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Settings className="h-5 w-5" />
            Trade-Specific Fields
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Additional fields required for your permit type.
            </p>

            {jobData.trade === 'electrical' || jobData.permitType === 'electrical' ? (
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="loadCalc">Load Calculation # <span className="text-destructive">*</span></Label>
                  <Input
                    id="loadCalc"
                    placeholder="From engineer/electrical calc"
                    value={((jobData as Record<string, unknown>).load_calculation as string) || ''}
                    onChange={(e) => handleUpdate('load_calculation', e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="panelSize">Panel Size (Amps) <span className="text-destructive">*</span></Label>
                  <Input
                    id="panelSize"
                    placeholder="e.g., 200"
                    value={((jobData as Record<string, unknown>).panel_size as string) || ''}
                    onChange={(e) => handleUpdate('panel_size', e.target.value)}
                  />
                </div>
              </div>
            ) : jobData.trade === 'mechanical' || jobData.permitType === 'mechanical' ? (
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="equipmentModel">Equipment Model <span className="text-destructive">*</span></Label>
                  <Input
                    id="equipmentModel"
                    placeholder="Manufacturer and model"
                    value={((jobData as Record<string, unknown>).equipment_model as string) || ''}
                    onChange={(e) => handleUpdate('equipment_model', e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="seer">SEER Rating <span className="text-destructive">*</span></Label>
                  <Input
                    id="seer"
                    placeholder="e.g., 16"
                    value={((jobData as Record<string, unknown>).seer_rating as string) || ''}
                    onChange={(e) => handleUpdate('seer_rating', e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="tonnage">Tonnage <span className="text-destructive">*</span></Label>
                  <Input
                    id="tonnage"
                    placeholder="e.g., 3"
                    value={((jobData as Record<string, unknown>).tonnage as string) || ''}
                    onChange={(e) => handleUpdate('tonnage', e.target.value)}
                  />
                </div>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground italic">
                Select a trade to see specific required fields.
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Unknown/Can't Provide */}
      <Card className={cn(currentSubstep === 'unknownHandling' && 'ring-2 ring-primary')}>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <HelpCircle className="h-5 w-5" />
            Missing Information
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              If you don't have certain information yet, mark it here so we can flag it for follow-up.
            </p>

            <div className="space-y-3">
              <div className="flex items-start gap-2">
                <Checkbox id="missingLoadCalc" className="mt-0.5" />
                <div>
                  <Label htmlFor="missingLoadCalc" className="text-sm font-medium cursor-pointer">
                    Waiting for load calculation from engineer
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    We'll remind you to add this before submission
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-2">
                <Checkbox id="missingOwnerInfo" className="mt-0.5" />
                <div>
                  <Label htmlFor="missingOwnerInfo" className="text-sm font-medium cursor-pointer">
                    Need to get property owner contact info
                  </Label>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
